import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToClient,
  mapClientWritePayload,
  type Client,
  type ClientApiResource,
  type ClientProfilePayload,
  type ClientWritePayload,
} from './clientsApi.types';

export async function fetchClients(
  page: number,
  pageSize: number,
  search: string,
  status: string
): Promise<PaginatedResult<Client>> {
  const trimmed = search.trim();
  const response = await httpClient.get<PaginatedApiResource<ClientApiResource>>('/clients', {
    params: {
      page,
      page_size: pageSize,
      search: trimmed.length === 0 ? undefined : trimmed,
      status_filter: status.length === 0 ? undefined : status,
    },
  });
  return mapPaginatedResource(response.data, mapApiResourceToClient);
}

export async function fetchClient(clientId: string): Promise<Client> {
  const response = await httpClient.get<ClientApiResource>('/clients/' + clientId);
  return mapApiResourceToClient(response.data);
}

export async function createClient(payload: ClientWritePayload): Promise<Client> {
  const response = await httpClient.post<ClientApiResource>(
    '/clients',
    mapClientWritePayload(payload)
  );
  return mapApiResourceToClient(response.data);
}

export async function updateClient(
  clientId: string,
  payload: ClientWritePayload
): Promise<Client> {
  const response = await httpClient.patch<ClientApiResource>(
    '/clients/' + clientId,
    mapClientWritePayload(payload)
  );
  return mapApiResourceToClient(response.data);
}

export async function archiveClient(clientId: string): Promise<Client> {
  const response = await httpClient.post<ClientApiResource>('/clients/' + clientId + '/archive');
  return mapApiResourceToClient(response.data);
}

export async function anonymiseClient(clientId: string, confirmation: string): Promise<Client> {
  const response = await httpClient.post<ClientApiResource>(
    '/clients/' + clientId + '/anonymise',
    { confirmation }
  );
  return mapApiResourceToClient(response.data);
}

export async function restoreClient(clientId: string): Promise<Client> {
  const response = await httpClient.post<ClientApiResource>('/clients/' + clientId + '/restore');
  return mapApiResourceToClient(response.data);
}

export interface ClientInviteResult {
  client: Client;
  inviteLink: string;
}

export async function inviteClient(clientId: string): Promise<ClientInviteResult> {
  const response = await httpClient.post<ClientApiResource & { invite_link: string }>(
    '/clients/' + clientId + '/invite'
  );
  return {
    client: mapApiResourceToClient(response.data),
    inviteLink: response.data.invite_link,
  };
}

export async function revokeClientInvite(clientId: string): Promise<Client> {
  const response = await httpClient.post<ClientApiResource>(
    '/clients/' + clientId + '/invite/revoke'
  );
  return mapApiResourceToClient(response.data);
}

export async function fetchOwnClientProfile(): Promise<Client> {
  const response = await httpClient.get<ClientApiResource>('/me/client-profile');
  return mapApiResourceToClient(response.data);
}

export async function updateOwnClientProfile(payload: ClientProfilePayload): Promise<Client> {
  const body: Record<string, unknown> = {};
  if (payload.fullName !== undefined) body.full_name = payload.fullName;
  if (payload.phone !== undefined) body.phone = payload.phone;
  if (payload.addressLine !== undefined) body.address_line = payload.addressLine;
  if (payload.city !== undefined) body.city = payload.city;
  if (payload.postalCode !== undefined) body.postal_code = payload.postalCode;
  if (payload.country !== undefined) body.country = payload.country;
  if (payload.preferredLanguage !== undefined) body.preferred_language = payload.preferredLanguage;
  const response = await httpClient.patch<ClientApiResource>('/me/client-profile', body);
  return mapApiResourceToClient(response.data);
}
